import React, { useCallback, useEffect, useRef } from 'react';

interface Iframe {
    iframeElement: HTMLIFrameElement;
    wrapperElement?: HTMLElement | null;
}

interface useIframeResizerProps {
    elementRef: React.RefObject<HTMLIFrameElement | HTMLElement>;
    delay?: number; // To account for cases where iframe content is loaded by React
}

export function useIframeResizer({ elementRef, delay = 0 }: useIframeResizerProps): () => void {
    const resizeObserverRefs = useRef<ResizeObserver[]>([]);
    const timeoutIds = useRef<NodeJS.Timeout[]>([]);

    const attachResizeObserver = useCallback(() => {
        const refElement = elementRef.current;
        if (!refElement) {
            return;
        }

        let iframes = new Array<Iframe>();

        if (refElement instanceof HTMLIFrameElement) {
            iframes.push({ iframeElement: refElement, wrapperElement: null });
        } else {
            // If the ref is a container, find all iframes within it
            iframes = Array.from(refElement.getElementsByTagName('iframe')).map((iframe) => {
                // ckeditor iframes are wrapped in divs with data-oembed-url attribute
                const iframeSrc = iframe.getAttribute('data-src') || iframe.src;
                const wrapperElement: HTMLElement | null = iframe.closest(`[data-oembed-url="${iframeSrc}"]`);
                return { iframeElement: iframe, wrapperElement };
            });
        }

        iframes.forEach((iframe) => {
            const iframeHandler = (): void => onIframeLoad(iframe);
            iframe.iframeElement.addEventListener('load', iframeHandler, { once: true });
        });

        const onIframeLoad = (iframe: Iframe): void => {
            const initIframeAutoResize = (): void => {
                const { iframeElement, wrapperElement } = iframe;
                try {
                    const body = iframeElement.contentWindow?.document.body;
                    if (body) {
                        // Adjust the height of the iframe based on the content
                        const resizeObserver = new ResizeObserver(() => {
                            iframeElement.style.height = `${body.scrollHeight}px`;
                            if (wrapperElement) {
                                wrapperElement.style.height = `${body.scrollHeight}px`;
                            }
                        });
                        resizeObserver.observe(body);
                        resizeObserverRefs.current.push(resizeObserver);
                    }
                } catch (error) {
                    console.warn('Cross-origin iframe; cannot access contents.', error);
                }
            };

            const timeoutId = setTimeout(() => initIframeAutoResize(), delay);
            timeoutIds.current.push(timeoutId);
        };
        return () => {
            if (resizeObserverRefs.current.length > 0) {
                resizeObserverRefs.current.forEach((observer) => observer.disconnect());
            }
            if (timeoutIds.current.length > 0) {
                timeoutIds.current.forEach((id) => clearTimeout(id));
            }
        };
    }, [delay, elementRef]);

    useEffect(() => {
        return attachResizeObserver();
    }, [attachResizeObserver]);

    return attachResizeObserver;
}
